import React from 'react'
import { Table, Tag, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuInfo } from 'rc-menu/lib/interface'
import { ordersApi } from 'api/orders'
import { ERoles } from 'models/auth'
import { EOrderStatus, IOrderTimeline, ITableOrder } from 'models/orders'
import { OrderDeadline } from 'components/orders/OrderDeadline/OrderDeadline'
import { OrderDateTime } from 'components/orders/OrderDateTime/OrderDateTime'
import { RefundFlowModal } from 'components/orders/RefundFlowModal/RefundFlowModal'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { getOrderStatusItems, orderTags } from 'shared/constants/orders'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { acceptOrderModal } from 'shared/utils/orders/acceptOrderModal'
import { pickOrderTimeline } from 'shared/utils/orders/pickOrderTimeline'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { showServerError } from 'shared/utils/showServerError'
import styles from './SuperOrderTable.module.scss'

interface ISuperOrderTableProps {
  orders?: ITableOrder[]
  isLoading?: boolean
  onOpenOrder: (orderId: string) => void
  onStatusChange: (
    orderId: string,
    status: EOrderStatus,
    timeline: IOrderTimeline
  ) => void
}

export const SuperOrderTable: React.FC<ISuperOrderTableProps> = ({
  orders,
  isLoading,
  onOpenOrder,
  onStatusChange
}) => {
  const isMobile = useIsMobile()
  const [isStatusUpdating, setIsStatusUpdating] = React.useState(false)

  const [refundModal, setRefundModal] = React.useState<{
    open: boolean
    status: EOrderStatus
    orderId?: string
  }>({ open: false, status: EOrderStatus.CANCELED })

  const changeStatus = async (
    status: EOrderStatus,
    orderId: string,
    options?: { throwOnError?: boolean }
  ) => {
    try {
      setIsStatusUpdating(true)
      const { data } = await ordersApi.changeStatus({ status, orderId })
      onStatusChange(orderId, status, pickOrderTimeline(data))
    } catch (e) {
      // При возврате ошибку показывает сама модалка возврата — пробрасываем её
      if (options?.throwOnError) throw e
      showServerError(e)
    } finally {
      setIsStatusUpdating(false)
    }
  }

  const closeRefundModal = () =>
    setRefundModal((prev) => ({ ...prev, open: false }))

  // Возврат/отмена из выпадающего списка статусов: единая модалка возврата
  const handleRefundConfirm = async () => {
    if (!refundModal.orderId) {
      return
    }
    await changeStatus(refundModal.status, refundModal.orderId, {
      throwOnError: true
    })
  }

  const onTagClick = (e: MenuInfo, orderId: string) => {
    if (e.key === EOrderStatus.CONFIRMED_BY_SELLER) {
      return acceptOrderModal(async () => {
        await changeStatus(EOrderStatus.CONFIRMED_BY_SELLER, orderId)
        bodyScrollLock.disable()
      })
    }

    if (e.key === EOrderStatus.CANCELED || e.key === EOrderStatus.RETURNED) {
      return setRefundModal({ open: true, status: e.key, orderId })
    }

    changeStatus(e.key as EOrderStatus, orderId)
  }

  const openOrderCell = ({ _id }: ITableOrder) => ({
    onClick: () => onOpenOrder(_id)
  })

  // Статус сам обрабатывает клик, открывать по нему заказ не нужно
  const stopClick = (event: React.MouseEvent) => event.stopPropagation()

  const columns: ColumnsType<ITableOrder> = React.useMemo(() => {
    const numberCell = ({ number }: ITableOrder) => (
      <Typography.Text style={{ wordWrap: 'break-word' }}>
        {number}
      </Typography.Text>
    )

    // Карточки открываем соседней вкладкой, чтобы не терять разобранный супер-заказ
    const sellerCell = ({ seller_id }: ITableOrder) =>
      seller_id?._id && seller_id.shop_details?.shop_name ? (
        <CardLink to={getSellerEditPath(seller_id._id)} newTab>
          {seller_id.shop_details.shop_name}
        </CardLink>
      ) : (
        <Typography.Text style={{ opacity: 0.5 }}>Удаленный аккаунт</Typography.Text>
      )

    // Без телефона аккаунт не удалён, а заведён по почте — показываем её
    const buyerCell = ({ user_id }: ITableOrder) =>
      user_id?._id ? (
        <Typography.Text>
          {user_id.tel ? phoneMaskStatic(user_id.tel) : user_id.email || 'Без телефона'}
        </Typography.Text>
      ) : (
        <Typography.Text style={{ opacity: 0.5 }}>Удаленный аккаунт</Typography.Text>
      )

    const dateCell = (order: ITableOrder) => (
      <OrderDateTime value={order.createdAt} />
    )

    const sumCell = ({ full_sum }: ITableOrder) => (
      <Typography.Text style={{ whiteSpace: 'nowrap' }}>
        {getNormalizedPrice(full_sum)}
      </Typography.Text>
    )

    const statusCell = (order: ITableOrder) => {
      const { status, _id, receive_method } = order
      if (
        status === EOrderStatus.CANCELED ||
        status === EOrderStatus.RETURNED ||
        status === EOrderStatus.NOT_PAID
      ) {
        return <Tag color={orderTags[status].color}>{orderTags[status].label}</Tag>
      }
      return (
        <div onClick={stopClick}>
          <StatusDropdown
            items={getOrderStatusItems(status, ERoles.ADMIN, receive_method)}
            label={orderTags[status as EOrderStatus].label}
            color={orderTags[status as EOrderStatus].color}
            onClick={(e) => onTagClick(e, _id)}
          />
          <OrderDeadline order={order} className={styles.deadline} />
        </div>
      )
    }

    // На телефоне те же колонки собраны в две: заказ с датой, продавцом и покупателем
    // и сумма со статусом — как в списке заказов
    if (isMobile) {
      return [
        {
          title: 'Заказ',
          dataIndex: 'number',
          width: '55%',
          onCell: openOrderCell,
          render: (_, record) => (
            <div className={styles.mobileCell}>
              <span className={styles.mobileNumber}>{numberCell(record)}</span>
              <Typography.Text type='secondary' className={styles.mobileSubText}>
                <OrderDateTime value={record.createdAt} inline />
              </Typography.Text>
              <span className={styles.mobileSubText}>{sellerCell(record)}</span>
              <span className={styles.mobileSubText}>{buyerCell(record)}</span>
            </div>
          )
        },
        {
          title: 'Сумма и статус',
          dataIndex: 'full_sum',
          width: '45%',
          onCell: openOrderCell,
          render: (_, record) => (
            <div className={styles.mobileCell}>
              {sumCell(record)}
              {statusCell(record)}
            </div>
          )
        }
      ]
    }

    return [
      {
        title: 'Заказ №',
        dataIndex: 'number',
        width: '12%',
        onCell: openOrderCell,
        render: (_, record) => numberCell(record)
      },
      {
        title: 'Продавец',
        dataIndex: 'seller',
        width: '21%',
        onCell: openOrderCell,
        render: (_, record) => sellerCell(record)
      },
      {
        title: 'Покупатель',
        dataIndex: 'buyer',
        width: '21%',
        onCell: openOrderCell,
        render: (_, record) => buyerCell(record)
      },
      {
        title: 'Дата и время',
        dataIndex: 'createdAt',
        width: '15%',
        onCell: openOrderCell,
        render: (_, record) => dateCell(record)
      },
      {
        title: 'Сумма',
        dataIndex: 'full_sum',
        width: '16%',
        onCell: openOrderCell,
        render: (_, record) => sumCell(record)
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: '15%',
        onCell: openOrderCell,
        render: (_, record) => statusCell(record)
      }
    ]
  }, [isMobile, orders])

  return (
    <>
      <Table
        scroll={isMobile ? undefined : { x: 850 }}
        size={isMobile ? 'small' : undefined}
        columns={columns}
        dataSource={orders}
        loading={isLoading || isStatusUpdating}
        rowKey={(el) => el._id}
        rowClassName='tableRow'
        pagination={false}
      />
      <RefundFlowModal
        open={refundModal.open}
        onConfirm={handleRefundConfirm}
        onClose={closeRefundModal}
      />
    </>
  )
}
